import { useCallback, useRef } from "react";

export const useAnimationFrameTicker = (
    tickFrequencyInMs: number,
    onTick: (elapsedTime: number) => void,
) => {
    const isActive = useRef<boolean>(false);

    const startTimeStamp = useRef<DOMHighResTimeStamp>(undefined);
    const previousTickTimeStamp = useRef<DOMHighResTimeStamp>(undefined);
    const animationFrameRequest = useRef<number>(undefined);

    const animationCb = useCallback(
        (timeStamp: DOMHighResTimeStamp) => {
            if (!startTimeStamp.current) {
                startTimeStamp.current = timeStamp;
            }

            if (!previousTickTimeStamp.current) {
                previousTickTimeStamp.current = timeStamp;
            }

            const elapsedSinceLastTick =
                timeStamp - previousTickTimeStamp.current;

            if (elapsedSinceLastTick >= tickFrequencyInMs) {
                previousTickTimeStamp.current = timeStamp;

                const totalElapsedTime = timeStamp - startTimeStamp.current;
                onTick(totalElapsedTime);
            }

            if (isActive.current) {
                animationFrameRequest.current =
                    requestAnimationFrame(animationCb);
            }
        },
        [onTick, tickFrequencyInMs],
    );

    const startTicker = useCallback(() => {
        isActive.current = true;

        animationFrameRequest.current = requestAnimationFrame(animationCb);
    }, [animationCb]);

    const stopTicker = useCallback(() => {
        isActive.current = false;

        if (animationFrameRequest.current) {
            cancelAnimationFrame(animationFrameRequest.current);
        }

        startTimeStamp.current = undefined;
        previousTickTimeStamp.current = undefined;
        animationFrameRequest.current = undefined;
    }, []);

    const restartTicker = useCallback(() => {
        const wasActive = isActive.current;

        stopTicker();

        if (wasActive) {
            startTicker();
        }
    }, [startTicker, stopTicker]);

    return {
        startTicker,
        stopTicker,
        restartTicker,
        isTicking: () => isActive.current,
    };
};
